import {
	memo,
	ReactElement,
	ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState
} from 'react';
import {
	TypographyComponent,
	TypographySize,
	TypographyVariant,
	TypographyWeight
} from '@vaa-component-lib/component.atom.typography';
import {
	ButtonColour,
	ButtonComponent,
	ButtonPriority,
	ButtonSize
} from '@vaa-component-lib/component.atom.button';
import debounce from 'lodash.debounce';

import {
	IconActionCrossComponent,
	IconFeedbackWarningComponent,
	IconsSize
} from '@vaa-component-lib/component.atom.icons';
import { BlanketComponent } from '@vaa-component-lib/component.atom.blanket';

import {
	PopOverButton,
	PopOverFooter,
	PopOverInnerPanel,
	PopOverHeader,
	PopOverPanel,
	PopOverPositioner,
	PopOverFeedbackPanel
} from './pop-over.styles';
import useMediaQuery, { BreakPointToken } from '../../hooks/useMediaQuery';
import { useBlanket } from '../../hooks/useBlanket';

import { Z_INDICES } from '../../lib/utils/constants';

export enum PopOverType {
	Default = 'default',
	Selector = 'selector'
}

export enum PopOverAlignment {
	Left = 'left',
	Right = 'right',
	Center = 'center'
}

export interface PopOverComponentProps {
	isVisible?: boolean;
	type?: PopOverType;
	alignment?: PopOverAlignment;
	scroll?: boolean;
	isOverflow?: boolean;
	dynamicHeight?: boolean;
	title?: string;
	id?: string;
	onClose?: () => void;
	children: ReactNode;
	maxHeight?: boolean;
	minWidth?: number;
	footerContent?: ReactElement;
	hasFooterBtn?: boolean;
	footerBtnText?: string;
	footerOnClick?: () => void;
	isFooterBtnDisabled?: boolean;
	variableHeight?: boolean;
}

const PopOverComponent = ({
	isVisible = false,
	type = PopOverType.Default,
	alignment = PopOverAlignment.Left,
	hasFooterBtn = true,
	scroll,
	title,
	footerContent,
	footerBtnText,
	footerOnClick,
	isFooterBtnDisabled,
	id = '',
	onClose = () => {},
	children,
	maxHeight = false,
	minWidth,
	isOverflow = false,
	dynamicHeight = false,
	variableHeight = false
}: PopOverComponentProps) => {
	const [isBlock, setIsBlock] = useState<boolean>(false);
	const [isAnimated, setIsAnimated] = useState<boolean>(false);
	const [isOverlapped, setIsOverlapped] = useState<boolean>(false);
	const animationTimeout = 200;

	const panelRef = useRef<HTMLElement>(null);
	const panelInnerRef = useRef<HTMLElement>(null);

	const isTablet = useMediaQuery(`(min-width: ${BreakPointToken.MdMin}px)`);
	const { blanket, setBlanket } = useBlanket();

	const updatePanelOverlapAndStyle = (panelRect: DOMRectReadOnly) => {
		const staticMaxHeight = 400;
		const overlapPadding = staticMaxHeight / 10;

		const {
			top: topEdge,
			left: leftEdge,
			right: rightEdge,
			bottom: bottomEdge
		} = panelRect;
		const { innerWidth, innerHeight } = window;

		const leftOverlap = leftEdge < 0 ? Math.abs(leftEdge) : 0;
		const rightOverlap =
			rightEdge > innerWidth ? Math.abs(rightEdge - innerWidth) : 0;
		const newIsOverlapped = leftOverlap > 0 || rightOverlap > 0;
		const bottomOverlap =
			bottomEdge > innerHeight ? Math.abs(bottomEdge - innerHeight) : 0;

		const calculatedTopOffset =
			innerHeight - (topEdge + bottomOverlap + overlapPadding);
		const innerStyle = panelInnerRef.current?.style;

		setIsOverlapped(newIsOverlapped);

		if (dynamicHeight && innerStyle) {
			if (staticMaxHeight < calculatedTopOffset) {
				innerStyle.removeProperty('max-height');
				innerStyle.removeProperty('overflow');
			}

			if (bottomOverlap > 0) {
				innerStyle.setProperty(
					'max-height',
					`calc((var(--calculated-vh) * 100) - ${topEdge + bottomOverlap + overlapPadding}px)`
				);
				innerStyle.setProperty('overflow', 'auto');
			}
		}
	};

	const handleOverlap = (entry: IntersectionObserverEntry) => {
		if (isTablet) {
			checkPanelPosition.current.cancel();
			const { boundingClientRect } = entry;
			updatePanelOverlapAndStyle(boundingClientRect);
		} else {
			setIsOverlapped(false);
		}
	};

	const checkPanelPosition = useRef(
		debounce((panelRect: DOMRectReadOnly) => {
			updatePanelOverlapAndStyle(panelRect);
		}, 50)
	);

	const checkVisibility = useCallback(() => {
		if (!panelRef.current) return;

		const panelRect = panelRef.current.getBoundingClientRect();

		if (panelRect.height !== 0 && panelRect.width !== 0) {
			checkPanelPosition.current(panelRect);
		} else {
			setTimeout(() => {
				if (!panelRef.current) return;

				checkPanelPosition.current(panelRef.current.getBoundingClientRect());
			}, animationTimeout);
		}
	}, [checkPanelPosition]);

	useEffect(() => {
		let animationTimer: NodeJS.Timeout;

		if (isVisible) {
			setIsBlock(true);
			animationTimer = setTimeout(
				() => setIsAnimated(true),
				animationTimeout / 2
			);
			setBlanket({
				...blanket,
				show: true,
				onClick: () => setBlanket({ ...blanket, show: false, onClick: null })
			});
		} else {
			setIsAnimated(false);
			setBlanket({ ...blanket, show: false });
			animationTimer = setTimeout(() => {
				setIsBlock(false);
				setIsOverlapped(false);
			}, animationTimeout);
		}

		return () => clearTimeout(animationTimer);
	}, [isVisible]);

	useEffect(() => {
		const panel = panelRef.current;

		if (!isVisible || !panel) {
			return;
		}

		const observerCallback: IntersectionObserverCallback = (entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting) {
					handleOverlap(entry);
				}
			});
		};

		const observer = new IntersectionObserver(observerCallback, {
			root: null,
			threshold: [0, 1.0]
		});

		observer.observe(panel);

		return () => {
			observer.disconnect();
		};
	}, [isVisible]);

	useEffect(() => {
		const handleResize = () => {
			if (isVisible && isTablet) {
				checkVisibility();
			}
		};

		handleResize();
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
			checkPanelPosition.current.cancel();
		};
	}, [isVisible, isTablet, checkVisibility]);

	useEffect(() => {
		const handleKeyUp = (e: KeyboardEvent) => {
			const { key } = e;

			if (key === 'Escape' && isVisible) {
				onClose();
			}
		};

		document.addEventListener('keyup', handleKeyUp);

		return () => document.removeEventListener('keyup', handleKeyUp);
	}, [isVisible, onClose]);

	useEffect(() => {
		if (!isTablet) {
			checkPanelPosition.current.cancel();
			setIsOverlapped(false);
		}
	}, [isTablet]);

	return (
		<>
			{!isTablet && (
				<BlanketComponent
					{...{ isVisible }}
					// @ts-ignore
					style={{ zIndex: Z_INDICES.popverBlanket }}
					blanketClicked={(): void => onClose()}
					controlScroll={false}
				/>
			)}
			<PopOverPositioner
				{...{ isBlock }}
				alignment={isOverlapped ? PopOverAlignment.Center : alignment}
				data-cy="popover-component-positioner">
				<PopOverPanel
					{...{ id, isBlock, type, isAnimated, minWidth }}
					onBlur={(e) => {
						if (
							!e.currentTarget.contains(e.relatedTarget) &&
							type === PopOverType.Selector
						) {
							onClose();
						}
					}}
					maxHeight={maxHeight}
					data-cy="popover-component"
					ref={panelRef}
					variableHeight={variableHeight}
					aria-live="polite">
					{!isTablet && (
						<PopOverHeader data-cy="popover-header-component" {...{ type }}>
							{title && (
								<TypographyComponent
									element="p"
									className="popover-title"
									variant={TypographyVariant.Heading}
									size={TypographySize.Sml}
									weight={TypographyWeight.Medium}>
									{title}
								</TypographyComponent>
							)}
							<PopOverButton>
								<ButtonComponent
									priority={ButtonPriority.Primary}
									colour={ButtonColour.ContrastSecondary}
									size={ButtonSize.XSmall}
									iconChild={<IconActionCrossComponent />}
									onClick={(): void => onClose()}
									aria-label="Close"
								/>
							</PopOverButton>
						</PopOverHeader>
					)}

					<PopOverInnerPanel {...{ isOverflow, scroll }} ref={panelInnerRef}>
						{children}
					</PopOverInnerPanel>

					{footerContent && (
						<PopOverFooter data-cy="popover-footer-component" {...{ type }}>
							<TypographyComponent
								element="div"
								variant={TypographyVariant.Body}
								size={TypographySize.Mdm}>
								{footerContent}
							</TypographyComponent>
							{hasFooterBtn && (
								<PopOverButton>
									<ButtonComponent
										priority={ButtonPriority.Primary}
										size={ButtonSize.Small}
										text={footerBtnText}
										onClick={footerOnClick}
										aria-label={footerBtnText}
										disabled={isFooterBtnDisabled}
									/>
								</PopOverButton>
							)}
						</PopOverFooter>
					)}
				</PopOverPanel>
			</PopOverPositioner>
		</>
	);
};

export const PopoverFeedback = ({ message }: { message: string }) => (
	<PopOverFeedbackPanel data-cy="popover-feedback">
		<IconFeedbackWarningComponent size={IconsSize.Sml} />
		<p>{message}</p>
	</PopOverFeedbackPanel>
);

export default memo(PopOverComponent);
